import React, { useMemo, useState } from 'react';
import { useParams, Link } from 'react-router-dom';
import { motion, AnimatePresence } from 'framer-motion';
import * as FiIcons from 'react-icons/fi';
import SafeIcon from '../common/SafeIcon';
import { useAnalytics } from '../contexts/AnalyticsContext';
import { format } from 'date-fns';
import HeatmapOverlay from './HeatmapOverlay';

const { 
  FiArrowLeft, FiMapPin, FiClock, FiShield, FiGlobe, 
  FiMonitor, FiZap, FiMousePointer, FiArrowDown, FiActivity,
  FiLayout, FiList
} = FiIcons;

const UserJourney = () => {
  const { userId } = useParams();
  const { analyticsData, customUsers } = useAnalytics();
  const [viewMode, setViewMode] = useState('list'); // 'list' or 'heatmap'

  const user = customUsers.find(u => u.user_serial === userId);

  const fullTimeline = useMemo(() => {
    const views = (analyticsData.pageViews || [])
      .filter(pv => pv.userSerial === userId || pv.visitorId === user?.linked_visitor_ids?.[0])
      .map(v => ({ ...v, type: 'pageview' }));
    const events = (analyticsData.events || [])
      .filter(e => e.userSerial === userId || e.visitorId === user?.linked_visitor_ids?.[0])
      .map(e => ({ ...e, type: 'event' }));
    return [...views, ...events].sort((a, b) => b.timestamp - a.timestamp);
  }, [analyticsData, userId, user]);

  const sessions = useMemo(() => {
    return fullTimeline.reduce((acc, current) => {
      const sessionId = current.sessionId || 'unknown';
      if (!acc[sessionId]) acc[sessionId] = [];
      acc[sessionId].push(current);
      return acc;
    }, {});
  }, [fullTimeline]);

  const getEventIcon = (item) => {
    if (item.type === 'pageview') return FiGlobe;
    if (item.eventName?.includes('click')) return FiMousePointer;
    if (item.eventName?.includes('scroll')) return FiArrowDown;
    return FiZap;
  };

  return (
    <div className="min-h-screen bg-slate-50 py-10">
      <div className="max-w-6xl mx-auto px-4">
        <div className="flex justify-between items-center mb-10">
          <Link to="/dashboard" className="inline-flex items-center text-indigo-600 font-black uppercase tracking-widest text-[10px] gap-2 hover:gap-3 transition-all">
            <SafeIcon icon={FiArrowLeft} /> Return to Intelligence
          </Link>
          
          <div className="flex bg-white p-1 rounded-2xl border border-slate-200 shadow-sm">
            <button 
              onClick={() => setViewMode('list')}
              className={`flex items-center gap-2 px-6 py-2.5 rounded-xl text-[10px] font-black uppercase tracking-widest transition-all ${viewMode === 'list' ? 'bg-indigo-600 text-white shadow-lg shadow-indigo-100' : 'text-slate-400 hover:text-slate-600'}`}
            >
              <SafeIcon icon={FiList} /> Timeline
            </button>
            <button 
              onClick={() => setViewMode('heatmap')}
              className={`flex items-center gap-2 px-6 py-2.5 rounded-xl text-[10px] font-black uppercase tracking-widest transition-all ${viewMode === 'heatmap' ? 'bg-indigo-600 text-white shadow-lg shadow-indigo-100' : 'text-slate-400 hover:text-slate-600'}`}
            >
              <SafeIcon icon={FiLayout} /> Spatial Heatmap
            </button>
          </div>
        </div>

        {/* Profile Header */}
        <div className="bg-white rounded-[3rem] shadow-2xl shadow-indigo-900/5 border border-slate-100 overflow-hidden mb-12">
          <div className="bg-gradient-to-br from-indigo-600 to-violet-800 h-48 relative overflow-hidden">
             <div className="absolute inset-0 opacity-10 bg-[url('https://images.unsplash.com/photo-1557683316-973673baf926?q=80&w=2000&auto=format&fit=crop')] bg-cover"></div>
          </div>
          <div className="px-12 pb-12 -mt-20 relative z-10">
            <div className="flex flex-col md:flex-row md:items-end justify-between gap-8">
              <div className="flex items-end gap-8">
                <div className="w-40 h-40 bg-white p-3 rounded-[3rem] shadow-2xl">
                  <div className="w-full h-full bg-indigo-50 rounded-[2.5rem] flex items-center justify-center text-indigo-600">
                    <SafeIcon icon={FiIcons.FiUser} className="w-20 h-20" />
                  </div>
                </div>
                <div className="mb-6">
                  <h1 className="text-5xl font-black text-slate-900 tracking-tight">
                    {user ? `${user.first_name} ${user.last_name}` : 'Identified Identity'}
                  </h1>
                  <p className="text-slate-400 font-mono text-sm font-bold mt-2 uppercase tracking-widest">
                    SID: {userId}
                  </p>
                </div>
              </div>
              <div className="flex gap-4 mb-4">
                <div className="bg-slate-50 px-8 py-5 rounded-[2rem] border border-slate-100 text-center">
                  <div className="text-[10px] font-black text-slate-400 uppercase tracking-widest mb-1">Status</div>
                  <div className="text-sm font-black text-green-600 uppercase tracking-widest">Active</div>
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* Multi-View Logic */}
        <AnimatePresence mode="wait">
          {viewMode === 'list' ? (
            <motion.div 
              key="list-view"
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              exit={{ opacity: 0, y: -20 }}
              className="space-y-12"
            >
              {Object.entries(sessions).map(([sessionId, items]) => (
                <div key={sessionId}>
                  <div className="flex items-center gap-6 mb-8">
                    <div className="bg-slate-900 text-white px-6 py-2 rounded-2xl text-[10px] font-black uppercase tracking-[0.2em]">
                      Session {sessionId.substring(0, 8)}
                    </div>
                    <div className="h-px bg-slate-200 flex-1"></div>
                  </div>
                  <div className="space-y-6 ml-8 border-l-2 border-slate-100 pl-12 relative">
                    {items.map((item, idx) => (
                      <div key={idx} className="relative bg-white p-8 rounded-[2rem] border border-slate-100 shadow-sm hover:shadow-xl transition-all">
                        <div className="absolute -left-[61px] top-8 w-10 h-10 bg-white border-2 border-slate-100 rounded-2xl flex items-center justify-center">
                          <SafeIcon icon={getEventIcon(item)} className="text-indigo-600 text-sm" />
                        </div>
                        <div className="flex justify-between items-start">
                          <div>
                            <span className="text-[10px] font-black uppercase text-slate-400 tracking-widest">{item.type}</span>
                            <h4 className="text-xl font-black text-slate-900 mt-1">{item.type === 'pageview' ? item.page : item.eventName}</h4>
                          </div>
                          <div className="text-right">
                            <div className="text-sm font-black text-slate-900">{format(item.timestamp, 'HH:mm:ss')}</div>
                            <div className="text-[10px] text-slate-400 font-bold uppercase tracking-widest">Recorded At</div>
                          </div>
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              ))}
            </motion.div>
          ) : (
            <motion.div 
              key="heatmap-view"
              initial={{ opacity: 0, scale: 0.95 }}
              animate={{ opacity: 1, scale: 1 }}
              exit={{ opacity: 0, scale: 0.95 }}
            >
              <div className="mb-8">
                <h2 className="text-3xl font-black text-slate-900 tracking-tight">Spatial Density Map</h2>
                <p className="text-slate-500 font-bold uppercase text-[10px] tracking-[0.2em] mt-1">Aggregated click coordinates across this user's lifecycle</p>
              </div>
              <HeatmapOverlay interactions={fullTimeline} />
            </motion.div>
          )}
        </AnimatePresence>
      </div>
    </div>
  );
};

export default UserJourney;